import React from 'react'

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, CircularProgress } from '@material-ui/core'
import asyncThrottler from '@scoir/async-throttler'
import { ControlPanel, LensControl, Month, Provider, TimezoneControl, Week } from '@scoir/react-calendar'
import { isEqual } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import qs from 'querystring'
import { connect } from 'react-redux'

import api from 'common/api'
import { selectVisibleCalendars } from 'store/linked-calendars'
import { actions as snackbarActions } from 'store/snackbar'
import { actions as calendarUpcomingVisitsActions } from 'store/upcoming-visits'

import { WeekView as CalendarEventWeekView } from './CalendarEvent'
import MonthDayEvents from './MonthDayEvents'
import { isSameTimeZone, shortTimeZone } from './helper'
import './index.scss'

const parseSearch = (search) => {
  search = search.replace(/^\?/, '')
  return qs.parse(search)
}
export class VisitsViewRoute extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      focus: moment(),
      isLoading: false,
      visits: [],
      activeTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZones: [],
    }
  }
  componentDidMount() {
    document.title = 'Scoir - Visits'
    const focus = this.createFocusMoment(this.props.location)
    this.throttler = asyncThrottler.create((state) => this.setState(state))
    this.loadVisits({ selectedSchools: this.props.selectedSchools, focus })
    this.getTimeZones()
  }
  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.selectedSchools, this.props.selectedSchools)) {
      this.loadVisits({ selectedSchools: this.props.selectedSchools })
      // Reset timezone when selected schools change IF the newly selected schools don't include the current active timezone
      if (!this.props.selectedSchools.some((school) => isSameTimeZone(school.TimeZone, this.state.activeTimezone))) {
        this.setState({ activeTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
      }
      this.getTimeZones()
    }
    if (!isEqual(prevProps.linkedCalendars, this.props.linkedCalendars)) {
      this.loadVisits({ linkedCalendars: this.props.linkedCalendars })
    }
    if (prevProps.location.search !== this.props.location.search) {
      const focus = this.createFocusMoment(this.props.location)
      this.loadVisits({ focus })
    }
    if (!isEqual(prevProps.lens, this.props.lens)) {
      this.loadVisits({ lens: this.props.lens })
    }
    if (!isEqual(prevProps.typeFilters, this.props.typeFilters)) {
      this.loadVisits({ typeFilters: this.props.typeFilters })
    }
  }
  getTimeZones() {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const zones = [{ timeZone: localTimeZone, isLocal: true }]

    this.props.selectedSchools.forEach((school) => {
      const exists = zones.some((g) => isSameTimeZone(school.TimeZone, g.timeZone))
      if (!exists) {
        zones.push({ timeZone: school.TimeZone, isLocal: false })
      }
    })

    const timezoneOptions = zones.map((zone) => ({
      value: zone.timeZone,
      label: zone.isLocal ? `Local Timezone (${shortTimeZone(zone.timeZone)})` : shortTimeZone(zone.timeZone),
      shortLabel: shortTimeZone(zone.timeZone),
    }))

    this.setState({ timeZones: timezoneOptions })
  }

  loadVisits({
    selectedSchools = this.props.selectedSchools,
    linkedCalendars = this.props.linkedCalendars,
    focus = this.state.focus,
    lens = this.props.lens,
    typeFilters = this.props.typeFilters,
  } = {}) {
    const startDate = moment(focus).startOf(lens).startOf('week').format()
    const endDate = moment(focus).endOf(lens).endOf('week').format()
    const params = qs.stringify({
      state: ['Draft', 'Open', 'Confirmed', 'Expired'],
      calendars: linkedCalendars.map((c) => c.id),
      ids: selectedSchools.map((s) => s.Id),
      startDate,
      endDate,
      virtual: typeFilters.virtual,
      collegeFair: typeFilters.collegeFair,
    })
    this.throttler.execute(
      api
        .get(`/api/highschools/calendar/visits?${params}`)
        .then((response) => {
          if (response.Error) {
            this.props.addSnack(response.Error, {
              id: 'view-visits-error',
              actionText: 'dismiss',
            })
          }
          return response.Visits
        })
        .then((rawVisits) => {
          this.props.fetchUpcomingVisits(this.props.userId)
          const visits = rawVisits.map((v) => {
            const visitId = v.Id || v.SyncId
            return {
              ...v,
              Id: visitId,
            }
          })
          this.setState({
            visits,
            focus,
          })
        }),
    )
  }
  changeLens(lens) {
    const ROOT_PATH = import.meta.env.BASE_URL
    const { history, location } = this.props
    history.replace(`${ROOT_PATH}/recruit/visits/view/${lens}${location.search}`)
  }
  createFocusMoment(location) {
    const query = parseSearch(location.search)
    if (query.focus) {
      return moment(query.focus, 'YYYYMMDD')
    }
    return this.state.focus
  }
  updateFocus(focus) {
    const { history, lens } = this.props
    const focusTime = focus.format('YYYYMMDD')
    history.replace(`${import.meta.env.BASE_URL}/recruit/visits/view/${lens}?focus=${focusTime}`)
  }
  render() {
    const FocusControl = this.props.lens === 'month' ? Month.FocusControl : Week.FocusControl
    return (
      <div className="route-visits">
        <ControlPanel className="calendar-cpanel-wrapper">
          <div className="calendar-cpanel">
            <div className="third-wrapper">
              <div className="lens-control-wrapper">
                <LensControl
                  lens={this.props.lens}
                  isVisible={['week', 'month']}
                  onLensChange={(lens) => this.changeLens(lens)}
                />
              </div>
              <div className="spinner-wrapper">
                {this.state.isProcessing && (
                  <CircularProgress
                    role="status"
                    aria-label="loading"
                    size="25px"
                  />
                )}
              </div>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => this.updateFocus(moment())}
              >
                Today
              </Button>
            </div>
            <div className="third-wrapper">
              <div className="focus-control-wrapper">
                <div className="focus-control-inline">
                  <FocusControl
                    focusDate={this.state.focus}
                    priorElem={
                      <span className="route-fa-icon">
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="sm"
                        />
                      </span>
                    }
                    aheadElem={
                      <span className="route-fa-icon">
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          size="sm"
                        />
                      </span>
                    }
                    onFocusDateChange={(newFocus) => this.updateFocus(newFocus)}
                  />
                </div>
              </div>
            </div>
            <div className="viewing-timezone">
              <div>
                <TimezoneControl
                  timezones={this.state.timeZones}
                  activeTimezone={this.state.activeTimezone}
                  onTimezoneChange={(newTimezone) => this.setState({ activeTimezone: newTimezone })}
                />
              </div>
            </div>
          </div>
        </ControlPanel>
        <Provider>
          {this.props.lens === 'week' && (
            <Week.View
              className="calendar-week-view"
              events={this.state.visits}
              focusDate={this.state.focus}
              eventComponent={(props) => (
                <CalendarEventWeekView
                  onEventChange={() => this.loadVisits()}
                  {...props}
                />
              )}
              timezone={this.state.activeTimezone}
            />
          )}
          {this.props.lens === 'month' && (
            <Month.View
              events={this.state.visits}
              focusDate={this.state.focus}
              eventDayComponent={(props) => {
                return (
                  <MonthDayEvents
                    onEventChange={() => this.loadVisits()}
                    {...props}
                  />
                )
              }}
            />
          )}
        </Provider>
      </div>
    )
  }
}

VisitsViewRoute.propTypes = {
  selectedSchools: PropTypes.arrayOf(PropTypes.object).isRequired,
  linkedCalendars: PropTypes.array.isRequired,
  fetchUpcomingVisits: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  lens: LensControl.propTypes.lens,
  addSnack: PropTypes.func.isRequired,
  typeFilters: PropTypes.object.isRequired,
}

export default connect(
  (state) => ({
    selectedSchools: state.visitSelectedSchools,
    linkedCalendars: selectVisibleCalendars(state),
    userId: state.user.record.Id,
    typeFilters: state.calendarVisitTypeFilters,
  }),
  {
    fetchUpcomingVisits: calendarUpcomingVisitsActions.fetchUpcomingVisits,
    addSnack: snackbarActions.addItem,
  },
)(VisitsViewRoute)
